import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';

export interface AnchoredMenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  align?: 'start' | 'end';
  className?: string;
  children: React.ReactNode;
}

/** Fixed-position menu anchored to a trigger; portals to body to escape overflow clipping. */
export function AnchoredMenu({
  open,
  onClose,
  anchorRef,
  align = 'start',
  className,
  children,
}: AnchoredMenuProps) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  useEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gap = 4;
      const top = r.bottom + gap;

      if (align === 'end') {
        setStyle({
          top,
          right: Math.max(8, window.innerWidth - r.right),
          left: 'auto',
          ...zStyle(Z_INDEX.portaledDropdown),
        });
        return;
      }

      const panelW = 220;
      let left = r.left;
      if (left + panelW > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - 8 - panelW);
      }
      setStyle({
        top,
        left,
        ...zStyle(Z_INDEX.portaledDropdown),
      });
    };

    update();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, anchorRef, align]);

  if (!open || !style || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0"
        style={zStyle(Z_INDEX.portaledDropdown - 1)}
        aria-hidden
        onClick={onClose}
      />
      <div
        className={cn('fixed rounded-lg border py-1 shadow-lg', className)}
        style={style}
        role="menu"
      >
        {children}
      </div>
    </>,
    document.body
  );
}

export default AnchoredMenu;
